/**
 * Build highlight segments for WebSearchResults.body from pii_matches offsets
 * (same visual language as webcontent/WebContentItem: yellow + underline + bold).
 */

export type TextSpan = { text: string; highlight: boolean };

export function mergeOverlappingRanges(
  ranges: [number, number][]
): [number, number][] {
  if (!ranges.length) {
    return [];
  }
  const sorted = [...ranges].sort((a, b) => a[0] - b[0]);
  const merged: [number, number][] = [];
  let cs = sorted[0][0];
  let ce = sorted[0][1];
  for (let i = 1; i < sorted.length; i++) {
    const [s, e] = sorted[i];
    if (s <= ce) {
      ce = Math.max(ce, e);
    } else {
      merged.push([cs, ce]);
      cs = s;
      ce = e;
    }
  }
  merged.push([cs, ce]);
  return merged;
}

export function segmentsFromOffsets(
  text: string,
  ranges: [number, number][]
): TextSpan[] {
  if (!text) {
    return [];
  }
  const merged = mergeOverlappingRanges(ranges);
  if (!merged.length) {
    return [{ text, highlight: false }];
  }
  const out: TextSpan[] = [];
  let last = 0;
  for (const [s, e] of merged) {
    const start = Math.max(0, Math.min(s, text.length));
    const end = Math.max(start, Math.min(e, text.length));
    if (last < start) {
      out.push({ text: text.slice(last, start), highlight: false });
    }
    if (start < end) {
      out.push({ text: text.slice(start, end), highlight: true });
    }
    last = Math.max(last, end);
  }
  if (last < text.length) {
    out.push({ text: text.slice(last), highlight: false });
  }
  return out.length ? out : [{ text, highlight: false }];
}

export function collectPiiOffsets(
  pii:
    | {
        names?: {
          text?: string;
          offsets?: { start: number; end: number }[];
        }[];
        institutions?: {
          text?: string;
          offsets?: { start: number; end: number }[];
        }[];
      }
    | null
    | undefined
): [number, number][] {
  const ranges: [number, number][] = [];
  if (!pii) {
    return ranges;
  }
  const buckets = [...(pii.names || []), ...(pii.institutions || [])];
  for (const ent of buckets) {
    for (const off of ent.offsets || []) {
      if (
        typeof off.start === "number" &&
        typeof off.end === "number" &&
        off.end > off.start
      ) {
        ranges.push([off.start, off.end]);
      }
    }
  }
  return ranges;
}
